import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import "./CustomForm.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  convertFormsToJson,
  renderRegistrationField,
  renderBaseField,
  renderTransferField,
  renderEditField,
  useGoogleMapsLoader,
} from "../../../util/utilfunctions/utilFunctions";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { generateValidationSchema } from "../../../constants/formvalidation";
import { setFormData } from "../../../store/redux/customForms";
import { stateData } from "../../../constants/countrylist";
import { setSelectedCountry } from "../../../store/redux/customForms";
import { Box, Typography } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import BaseButton from "../BaseButton/BaseButton";

// Conditional fields will look like something below, where you have to watch the field that determines what gets rendered or not
// const conditionalFields = {
//     cardNumber: (value, watch) => watch("accountType") === "313",
//     friendlyName: (value, watch) => watch("accountType") === "313",
//     purpose: (value, watch) => watch("accountType") === "313",
//     fee: (value, watch) => watch("accountType") === "313",
//   };

const CustomForm = forwardRef(
  (
    {
      onSubmit, // Function to run when form is submitted
      formName, // Name of the form to fetch from the backend
      resetData, // Boolean to determine if form should be reset after submission
      formValues, // Object containing form values to populate the form with (key should match name in database)
      twoColumns, // Boolean to determine if form should be displayed in two columns
      fieldRendererType, // Type of field renderer to use
      buttonText, // Text to display on the submit button
      loadingButtonText, // Text to display on the submit button when loading
      conditionalFields, // Object containing conditional fields
      buttonAlignment = "flex-end", // Default alignment using display flex
      useStep = false, // Callback function to pass form data back to the parent component
      tabSort = [], // Added tabSort prop for custom step wizard
      formGroup = "",
      option = [],
      onNext, // New prop for handling the "Next" button click
      onPrevious, // New prop for handling the "Previous" button click
      handleContactChange,
      triggerFields = [],
      panelIndex,
      containerText = "",
      buttonInPanel = false,
      disable = false,
      roles = [],
      renderButton = true,
      isEdit = false,
      isRegistration = false,
      conditionalGroups = {},
      customerTypes,
      customerSubTypes,
      setSelectedCustomerType,
      onFieldClick,
      handleAddBussinessToggleBit
    },
    ref
  ) => {
    const [loading, setLoading] = useState(false);
    const [loadForms, setLoadForms] = useState(false);
    const formDataFromCache = useSelector((state) => state.customForms.formCache[formName] || []);
    const [buttonPressed, setButtonPressed] = useState(false);
    const configVariables = useSelector((state) => state.config.baseConfig);

    // console.log("formValuessss--------------", formValues);
    // console.log("customerTypes--------------", customerTypes);
    // console.log("customerSubTypes--------------", customerSubTypes);

    // console.log(formData)
    const [stateOptions, setStateOptions] = useState({
      stateid: [],
      birthstate: [],
      businessstateid: [],
      state: [],
      businessstate: [],
      receiverstate: [],
    });

    const dispatch = useDispatch();

    // const [autocomplete, setAutocomplete] = useState(null);
    const autocompleteRef = useRef(null);
    const { isLoaded } = useGoogleMapsLoader();

    const fieldRenderers = {
      registration: (field, selectOption, register, errors, fieldValue) =>
        isEdit
          ? renderEditField(
              field,
              selectOption,
              register,
              errors,
              stateOptions,
              setValue,
              trigger,
              autocompleteRef,
              getValues,
              isLoaded,
              disable,
              roles,
              fieldValue,
              control,
              buttonPressed,
              customerTypes,
              customerSubTypes,
              setSelectedCustomerType,
              configVariables
            )
          : renderRegistrationField(
              field,
              register,
              errors,
              stateOptions,
              setValue,
              trigger,
              autocompleteRef,
              getValues,
              isLoaded,
              disable,
              roles,
              buttonPressed,
              customerTypes,
              customerSubTypes,
              setSelectedCustomerType,
              handleAddBussinessToggleBit,
              configVariables
            ),
      base: renderBaseField,
      default: (field, selectOption, register, errors) =>
        renderBaseField(
          field,
          selectOption,
          register,
          errors,
          getValues,
          setValue
        ),
      transfer: (field, selectOption, register, errors) =>
        renderTransferField(
          field,
          selectOption,
          register,
          errors,
          handleContactChange
        ),
    };

    let formData = [...formDataFromCache];

    if (formName === "newagent") {
      const countryIndex = formData.findIndex((field) => field.name === "country");

      if (countryIndex > -1) {
        const stateField = {
          name: "stateid",
          label: "State",
          type: "select",
          validation: { required: true },
          core: 1,
          formgroup: formData[countryIndex].formgroup,
          tabSort: formData[countryIndex].tabSort,
        };
        formData.splice(countryIndex + 1, 0, stateField);
      }
    }

    const filteredFormData = formData.filter((field) =>
      tabSort.includes(field.tabSort)
    );
    const validationSchema = generateValidationSchema(filteredFormData);

    const {
      control,
      register,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
      watch,
      getValues,
      trigger,
      setFocus,
    } = useForm({
      resolver: yupResolver(validationSchema),
      mode: "onChange",
    });

    const selectedCountry = watch("country");
    const selectedBirthCountry = watch("birthcountry");
    const selectedBusinessCountry = watch("businesscountryidlist");
    const selectedbusinessCountry = watch("businesscountry");
    const receiverCountry = watch("receivercountry");
    const receivingCountry = watch("receivingcountry");

    const normalizeToggleFields = (values) => {
      return Object.entries(values).reduce((acc, [key, value]) => {
        const isToggleField = formData.some(
          (field) => field.name === key && field.type === "toggle"
        );
        acc[key] = isToggleField
          ? value === true || value === "true" || value === "1"
          : value;
        return acc;
      }, {});
    };

    // Watch all form values
    const watchedFields = watch();

    // Ref to keep track of previous values
    const previousValuesRef = useRef({});

    useEffect(() => {
      const currentValues = getValues();
      const previousValues = previousValuesRef.current;

      // console.log(currentValues, previousValues)
      // console.log(currentValues)

      // const normalizedValues = normalizeBooleans(currentValues);
      const normalizedValues = normalizeToggleFields(currentValues); // Normalize toggle fields only

      const hasValuesChanged =
        JSON.stringify(normalizedValues) !== JSON.stringify(previousValues);

      if (hasValuesChanged) {
        previousValuesRef.current = normalizedValues; // Update ref with current values

        if (onNext) {
          onNext(normalizedValues); // Always call onNext when any field changes
        }
      }
    }, [watchedFields, getValues, triggerFields]);

    useEffect(() => {
      // Function to update options based on the watched fields
      const updateStateOptions = (fieldValue, type) => {
        const states =
          stateData.find((country) => country.iso3 === fieldValue)?.states ||
          [];
        if (states.length === 0) {
          return [{ key: "na", value: "na", label: "No State Available" }];
        }
        return states.map((state) => ({
          key: state.state_code,
          value: state.state_code,
          label: state.name,
        }));
      };

      setStateOptions({
        stateid: selectedCountry
          ? updateStateOptions(selectedCountry, "stateid")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
        birthstate: selectedBirthCountry
          ? updateStateOptions(selectedBirthCountry, "birthstate")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
        businessstateid: selectedBusinessCountry
          ? updateStateOptions(selectedBusinessCountry, "businessstateid")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
        businessstate: selectedbusinessCountry
          ? updateStateOptions(selectedbusinessCountry, "businessstate")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
        receiverstate: receiverCountry
          ? updateStateOptions(receiverCountry, "receiverstate")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
        receivingstate: receivingCountry
          ? updateStateOptions(receivingCountry, "receivingstate")
          : [
              {
                key: "not-available",
                value: "not-available",
                label: "Not Available",
              },
            ],
      });
    }, [
      selectedCountry,
      selectedBirthCountry,
      selectedBusinessCountry,
      selectedbusinessCountry,
      receiverCountry,
      receivingCountry,
    ]);

    useEffect(() => {
      const fetchForm = async () => {
        try {
          const response = await fetch(`/forms/${formName}.json`);
          const formResponseData = await response.json();
          const convertedForms = convertFormsToJson(formResponseData);
          // console.log("convertedForms----------",convertedForms);
          dispatch(setFormData({ formName, data: convertedForms }));
        } catch (error) {
          console.error("Failed to fetch form data:", error);
        }
      };

      fetchForm();
    }, [dispatch, formName]);

    const submitForm = async (e) => {
      setLoading(true);
      setButtonPressed(true);
      const values = getValues();
      // console.log(formData);
      // console.log(values);

      try {
        const response = await onSubmit(e, formData, values);
        if (response && resetData === true) {
          reset({});
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setLoading(false);
      }
    };

    function decodeHtmlEntities(str) {
      if (typeof str !== "string") return str;
    
      const txt = document.createElement("textarea");
      let prev = str;
      let decoded = str;
    
      do {
        prev = decoded;
        txt.innerHTML = prev;
        decoded = txt.value;
      } while (decoded !== prev);
    
      return decoded;
    }    

    // useEffect(() => {
    //   if (formValues) {
    //     Object.keys(formValues).forEach((key) => {
    //       setValue(key, formValues[key], { shouldValidate: true });
    //     });
    //   }
    // }, [formValues, setValue]);

    useEffect(() => {
      if (formValues) {
        Object.keys(formValues).forEach((key) => {
          const rawValue = formValues[key];
          const decodedValue =
            typeof rawValue === "string" ? decodeHtmlEntities(rawValue) : rawValue;
    
          setValue(key, decodedValue, { shouldValidate: true });
        });
      }
    }, [formValues, setValue]);
    

    const groupedFields = formData.reduce((acc, field) => {
      const group = field.formgroup;
      if (group) {
        acc[group] = acc[group] || [];
        acc[group].push(field);
      } else {
        acc.nogroup = acc.nogroup || [];
        acc.nogroup.push(field);
      }
      return acc;
    }, {});

    const filteredGroupedFields =
      formGroup && groupedFields[formGroup]
        ? { [formGroup]: groupedFields[formGroup] }
        : {};

    useImperativeHandle(ref, () => ({
      trigger,
      setFocus,
      watch,
      triggerValidation: () => trigger(),
      resetForm() {
        reset();
      },
      getValues() {
        return getValues();
      },
      validateAndSetErrors: async () => {
        const isValid = await trigger(); // Trigger validation for all fields
        setButtonPressed(true);

        if (!isValid) {
          // Iterate through errors and log them or update as needed
          const fieldErrors = Object.entries(errors).reduce(
            (acc, [fieldName, errorDetails]) => {
              if (errorDetails?.type === "required") {
                acc[fieldName] = "This field is required"; // Set custom error message
              }
              return acc;
            },
            {}
          );

          console.log("Field Errors:", fieldErrors); // Log or handle errors as needed
          return fieldErrors;
        }

        return {}; // No errors
      },
    }));

    const toggleFields = [];

    const watchFields = watch(conditionalFields);

    // const isXLScreen = useMediaQuery("(min-width:1536px)");
    const isXLScreen = useMediaQuery("(min-width:2560px)");

    useEffect(() => {
      adjustFieldsetHeights(isXLScreen); // Adjust heights based on screen size

      // Recalculate on window resize
      window.addEventListener("resize", () =>
        adjustFieldsetHeights(isXLScreen)
      );

      // Cleanup listener on unmount
      return () =>
        window.removeEventListener("resize", () =>
          adjustFieldsetHeights(isXLScreen)
        );
    }, [watchFields, isXLScreen]);

    function adjustFieldsetHeights(isXLScreen) {
      const fieldsets = document.querySelectorAll(".registration-fieldset");

      if (fieldsets.length === 0) return;

      // Calculate the maximum height if the screen is not XL
      let maxHeight = 0;

      if (!isXLScreen) {
        fieldsets.forEach((fieldset) => {
          fieldset.style.minHeight = "auto"; // Reset to auto to recalculate heights
          const fieldsetHeight = fieldset.offsetHeight;
          maxHeight = Math.max(maxHeight, fieldsetHeight);
        });
      }

      // Apply either the calculated maxHeight or a fixed height for XL screens
      fieldsets.forEach((fieldset) => {
        // console.log("maxHeight=========",maxHeight);
        fieldset.style.minHeight = isXLScreen ? "525px" : `${maxHeight + 10}px`;
      });
    }

    // console.log(formData);
    // console.log(groupedFields, filteredGroupedFields)

    return (
      <>
        <Box>
          {!loadForms && (
            <Box>
              <form onSubmit={handleSubmit(submitForm)} noValidate>
                {Object.keys(groupedFields)
                  .filter((group) =>
                    conditionalGroups[group]
                      ? conditionalGroups[group](watch)
                      : true
                  )
                  .map((group, groupIndex, filteredGroups) => {
                    const fields =
                      useStep && formGroup === group
                        ? filteredGroupedFields[group]
                        : groupedFields[group];

                    if (formGroup && formGroup !== group) return null;

                    // Dynamically check if this group should be rendered using bracket notation for keys with spaces
                    const shouldRenderGroup = conditionalGroups[group]
                      ? conditionalGroups[group](watch)
                      : true;

                    // Check if this is the last group to render the submit button there
                    const isLastGroup =
                      groupIndex === filteredGroups.length - 1;

                    // Skip rendering this fieldset if the group does not meet the condition
                    if (!shouldRenderGroup) return null;

                    return group === "nogroup" ? (
                      fields.map((field, index) => {
                        const fieldValue = getValues(field.name);

                        const renderField =
                          fieldRenderers[fieldRendererType] ||
                          fieldRenderers.default;

                        if (field.name === "country") {
                          dispatch(setSelectedCountry(field.value));
                        }

                        const shouldRender =
                          conditionalFields && conditionalFields[field.name]
                            ? conditionalFields[field.name](field.value, watch)
                            : true;

                        if (!shouldRender) return null;

                        if (twoColumns && index % 2 === 0) {
                          let nextField = fields[index + 1];

                          const nextShouldRender =
                            nextField &&
                            (conditionalFields &&
                            conditionalFields[nextField.name]
                              ? conditionalFields[nextField.name](
                                  nextField.value,
                                  watch
                                )
                              : true);

                          if (!nextShouldRender) nextField = null;

                          const fieldValue = getValues(field.name);

                          return (
                            <Box className="signup-row" key={field.name}>
                              {/* {console.log(errors)} */}
                              {renderField(
                                field,
                                option,
                                register,
                                errors,
                                fieldValue
                              )}
                              {nextField &&
                                renderField(
                                  nextField,
                                  option,
                                  register,
                                  errors,
                                  fieldValue
                                )}
                            </Box>
                          );
                        }

                        if (twoColumns && index % 2 !== 0) {
                          return null;
                        }

                        return (
                          <Box key={field.name}>
                            {renderField(
                              field,
                              option,
                              register,
                              errors,
                              fieldValue
                            )}
                          </Box>
                        );
                      })
                    ) : (
                      <fieldset
                        key={group}
                        className={`${
                          isRegistration
                            ? "registration-fieldset"
                            : "fieldset-container"
                        }`}
                      >
                        <Typography
                          variant="h9"
                          className="legend-label"
                          sx={{
                            fontFamily: "Nunito",
                            fontStyle: "normal",
                            fontWeight: 600,
                            lineHeight: "60px",
                            color: "#00365A",
                          }}
                        >
                          {group}
                        </Typography>
                        {fields.map((field, index) => {
                          const fieldValue = getValues(field.name);
                          
                          const renderField =
                          fieldRenderers[fieldRendererType] ||
                          fieldRenderers.default;
                          const shouldRender =
                          conditionalFields && conditionalFields[field.name]
                          ? conditionalFields[field.name](
                            field.value,
                            watch
                            )
                            : true;
                            
                            if (!shouldRender) return null;
                            
                            if (field.type === "toggle") {
                              toggleFields.push(field);
                              return null; // Do not render the toggle field here, it'll be rendered later
                            }
                            // console.log("shouldRender----------------------====================",field)
                            // console.log("shouldRender----------------------",index)
                            
                            if (twoColumns && index % 2 === 0) {
                              let nextField = fields[index + 1];

                            const nextShouldRender =
                              nextField &&
                              (conditionalFields &&
                              conditionalFields[nextField.name]
                                ? conditionalFields[nextField.name](
                                    nextField.value,
                                    watch
                                  )
                                : true);
                                
                            if (
                              !nextShouldRender ||
                              nextField.type === "toggle"
                            )
                              nextField = null;
                              // console.log("shouldRender---------------------",shouldRender)
                            return (
                              <Box
                                className="signup-row"
                                key={field.name}
                                id={field.name}
                                onClick={() => onFieldClick && onFieldClick(field)}
                              >
                                {renderField(
                                  field,
                                  option,
                                  register,
                                  errors,
                                  fieldValue
                                )}
                                {nextField &&
                                  renderField(
                                    nextField,
                                    option,
                                    register,
                                    errors,
                                    fieldValue
                                  )}
                              </Box>
                            );
                          }

                          if (twoColumns && index % 2 !== 0) {
                            return null;
                          }

                          return (
                            <Box key={field.name}>
                              {renderField(
                                field,
                                option,
                                register,
                                errors,
                                fieldValue
                              )}
                            </Box>
                          );
                        })}

                        <Box>
                          {toggleFields.map((field, index) => {
                            const fieldValue = getValues(field.name);
                            const renderField =
                              fieldRenderers[fieldRendererType] ||
                              fieldRenderers.default;

                            if (
                              toggleFields.length === 0 ||
                              field.formgroup !== group
                            )
                              return null;

                            if (index % 2 === 0) {
                              let nextToggle = toggleFields[index + 1];
                              return (
                                <Box
                                  className="signup-row"
                                  key={field.name}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    gap: "5rem",
                                  }}
                                  onClick={() => onFieldClick && onFieldClick(field)}
                                >
                                  {renderField(
                                    field,
                                    option,
                                    register,
                                    errors,
                                    fieldValue
                                  )}
                                  {nextToggle &&
                                    renderField(
                                      nextToggle,
                                      option,
                                      register,
                                      errors,
                                      fieldValue
                                    )}
                                </Box>
                              );
                            }

                            return null;
                          })}
                        </Box>

                        <Box
                          className="custom-button-container"
                          style={{
                            display: "flex",
                            justifyContent: "right",
                            alignItems: "right",
                            marginTop: "20px",
                          }}
                        >
                          {getValues().ismerchant === true &&
                            panelIndex == 1 && (
                              <Typography
                                variant="h9"
                                sx={{ fontSize: "13px", color: "#00365A" }}
                              >
                                Please complete Business Information &gt;
                              </Typography>
                            )}
                        </Box>

                        <Box
                          className="custom-button-container"
                        >
                          {renderButton &&
                            onSubmit &&
                            buttonInPanel &&
                            isLastGroup && (
                              <BaseButton
                                buttonText={
                                  loading ? loadingButtonText : buttonText
                                }
                                type="submit"
                                disabled={loading}
                                buttonStyle="add-new-button"
                              />
                            )}
                        </Box>
                      </fieldset>
                    );
                  })}
                <Box
                  className="custom-button-container"
                  style={{
                    marginTop: "20px",
                  }}
                >
                  {renderButton && onSubmit && !buttonInPanel && (
                    <BaseButton
                      buttonText={loading ? loadingButtonText : buttonText}
                      type="submit"
                      disabled={loading}
                      buttonStyle="add-new-button"
                    />
                  )}
                </Box>
              </form>
            </Box>
          )}
        </Box>
      </>
    );
  }
);

export default CustomForm;